<template>
  <div class="table-custom panel-card">
    <div class="table-custom__toolbar" v-if="showFilter || true">
      <div v-if="showFilter" class="table-custom__search">
        <el-input v-model="internalFilter.text" clearable placeholder="Type to search" @clear="currentPage = 1">
          <el-button slot="append" icon="el-icon-close" @click="internalFilter.text = ''">Clear</el-button>
        </el-input>
      </div>

      <div class="table-custom__pagination-controls">
        <div class="table-custom__per-page">
          <span>Per page</span>
          <el-select v-model="perPage" size="small" class="table-custom__per-page-select">
            <el-option v-for="option in normalizedPageOptions" :key="option.value" :label="option.text" :value="option.value" />
          </el-select>
        </div>
      </div>
    </div>

    <el-table
      :data="pagedItems"
      v-loading="loading"
      stripe
      border
      @sort-change="handleSortChange"
      empty-text="No data"
    >
      <el-table-column
        v-for="field in fields"
        :key="field.key"
        :prop="field.key"
        :label="field.label"
        :sortable="field.sortable ? 'custom' : false"
        :min-width="field.key === 'actions' ? 220 : 140"
        show-overflow-tooltip
      >
        <template slot-scope="scope">
          <div v-if="field.key === 'actions'" class="table-custom__actions">
            <el-button
              v-if="scope.row.actions && scope.row.actions.edit ? scope.row.actions.edit.show : false"
              :disabled="loading"
              type="primary"
              size="mini"
              @click="editItem(scope.row)"
            >
              {{ scope.row.actions.edit ? scope.row.actions.edit.name : 'Edit' }}
            </el-button>
            <el-button
              v-if="scope.row.actions && scope.row.actions.delete ? scope.row.actions.delete.show : false"
              :disabled="loading"
              type="danger"
              size="mini"
              @click="deleteItem(scope.row)"
            >
              {{ scope.row.actions.delete ? scope.row.actions.delete.name : 'Delete' }}
            </el-button>
            <el-button
              v-if="scope.row.actions && scope.row.actions.show ? scope.row.actions.show.show : false"
              :disabled="loading"
              size="mini"
              @click="showItem(scope.row)"
            >
              {{ scope.row.actions.show ? scope.row.actions.show.name : 'Show' }}
            </el-button>
          </div>
          <span v-else>{{ formatValue(field, scope.row) }}</span>
        </template>
      </el-table-column>
    </el-table>

    <div class="table-custom__footer">
      <el-pagination
        background
        layout="prev, pager, next, total"
        :current-page.sync="currentPage"
        :page-size="perPage"
        :total="totalRows"
      />
    </div>
  </div>
</template>

<script>
export default {
  props: {
    items: {
      type: Array,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    showFilter: {
      type: Boolean,
      default: true,
    },
    filter: {
      type: Object,
      default() {
        return { text: '' }
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, { value: 100, text: 'Show a lot' }],
      sortBy: '',
      sortDirection: 'asc',
      internalFilter: {
        text: '',
      },
    }
  },
  computed: {
    normalizedPageOptions() {
      return this.pageOptions.map((option) => {
        if (typeof option === 'object') return option
        return { value: option, text: String(option) }
      })
    },
    activeFilterText() {
      return (this.filter && this.filter.text != null ? this.filter.text : this.internalFilter.text || '').toString().toLowerCase()
    },
    filteredItems() {
      if (!this.activeFilterText) return this.items

      const searchableKeys = this.fields.filter((field) => field.key !== 'actions').map((field) => field.key)
      return this.items.filter((item) =>
        searchableKeys.some((key) => String(item[key] != null ? item[key] : '').toLowerCase().includes(this.activeFilterText))
      )
    },
    sortedItems() {
      if (!this.sortBy) return this.filteredItems

      return [...this.filteredItems].sort((a, b) => {
        const left = a[this.sortBy]
        const right = b[this.sortBy]
        if (left === right) return 0
        if (this.sortDirection === 'descending') {
          return left > right ? -1 : 1
        }
        return left > right ? 1 : -1
      })
    },
    pagedItems() {
      const start = (this.currentPage - 1) * this.perPage
      return this.sortedItems.slice(start, start + this.perPage)
    },
    totalRows() {
      return this.sortedItems.length
    },
  },
  watch: {
    totalRows() {
      if ((this.currentPage - 1) * this.perPage >= this.totalRows && this.currentPage > 1) {
        this.currentPage = 1
      }
    },
    'filter.text': {
      immediate: true,
      handler(value) {
        this.internalFilter.text = value || ''
      },
    },
  },
  methods: {
    formatValue(field, row) {
      if (typeof field.formatter === 'function') {
        return field.formatter(row[field.key], field.key, row)
      }
      return row[field.key]
    },
    handleSortChange({ prop, order }) {
      this.sortBy = prop || ''
      this.sortDirection = order || 'asc'
    },
    editItem(item) {
      this.$emit('editObject', item)
    },
    showItem(item) {
      this.$emit('showObject', item)
    },
    deleteItem(item) {
      this.$emit('deleteObject', item)
    },
  },
}
</script>

<style scoped>
.table-custom {
  padding: 18px;
}

.table-custom__toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.table-custom__search {
  flex: 1;
  min-width: 240px;
}

.table-custom__pagination-controls {
  display: flex;
  justify-content: flex-end;
}

.table-custom__per-page {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #606266;
}

.table-custom__per-page-select {
  width: 120px;
}

.table-custom__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.table-custom__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
</style>
